/**
 * Player-level QoE metrics that the P2P engine cannot see: startup time
 * (time-to-first-frame) and rebuffering (count + total stalled duration).
 *
 * These come purely from HTMLMediaElement events. The initial buffering before
 * the first frame is startup, NOT a rebuffer — we only count stalls that happen
 * after playback has begun.
 */
export interface PlaybackSnapshot {
  startupMs: number | null; // null until first frame renders
  rebufferCount: number;
  rebufferMs: number;
  stalled: boolean;
}

export class PlaybackMetrics {
  private playRequestedAt: number | null = null;
  private startupMs: number | null = null;
  private started = false;
  private rebufferCount = 0;
  private rebufferMs = 0;
  private stallStart: number | null = null;

  constructor(private readonly video: HTMLVideoElement) {
    video.addEventListener("play", () => {
      if (this.playRequestedAt === null) this.playRequestedAt = now();
    });
    // First real frame → startup complete.
    video.addEventListener("playing", () => {
      if (!this.started && this.playRequestedAt !== null) {
        this.started = true;
        this.startupMs = now() - this.playRequestedAt;
      }
      this.endStall();
    });
    // A stall AFTER startup is a rebuffer.
    video.addEventListener("waiting", () => {
      if (this.started && this.stallStart === null) {
        this.stallStart = now();
        this.rebufferCount++;
      }
    });
    video.addEventListener("timeupdate", () => this.endStall());
  }

  private endStall(): void {
    if (this.stallStart !== null) {
      this.rebufferMs += now() - this.stallStart;
      this.stallStart = null;
    }
  }

  snapshot(): PlaybackSnapshot {
    // Include the in-progress stall so the number climbs live.
    const live = this.stallStart !== null ? now() - this.stallStart : 0;
    return {
      startupMs: this.startupMs,
      rebufferCount: this.rebufferCount,
      rebufferMs: Math.round(this.rebufferMs + live),
      stalled: this.stallStart !== null,
    };
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
